import React from 'react';
import { Link } from 'react-router-dom';

/**
 * ログイン・登録・パスワード再設定など、認証まわりの画面に共通の枠。
 *
 * これらは「顧客がキンログに最初に触れる画面」でありながら、素の白背景に
 * `<h1>ログイン</h1>` が置かれただけで、どのサービスの画面か分からなかった。
 * LP と同じパレット（#2563EB 基調）とロゴを載せ、同じ製品だと分かるようにする。
 *
 * 各ページは中身のフォームだけを children として渡す。
 */
interface AuthLayoutProps {
  title: string;
  /** 見出しの下に置く補足（任意） */
  description?: React.ReactNode;
  children: React.ReactNode;
  /** カード下部に置く導線（「アカウントをお持ちの方」等） */
  footer?: React.ReactNode;
}

const BRAND = '#2563EB';
const BRAND_DARK = '#1D4ED8';
const INK = '#1E293B';
const MUTED = '#64748B';

const AuthLayout: React.FC<AuthLayoutProps> = ({ title, description, children, footer }) => (
  <div
    style={{
      minHeight: '100vh',
      background: `linear-gradient(160deg, #F1F5F9 0%, #E2E8F0 55%, #DBEAFE 100%)`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: 'clamp(24px, 6vw, 56px) 16px',
      fontFamily: "'Zen Kaku Gothic New', -apple-system, BlinkMacSystemFont, sans-serif",
    }}
  >
    {/* ロゴ。押すと LP へ戻れるようにしておく（迷子の出口） */}
    <Link
      to="/"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        textDecoration: 'none',
        marginBottom: 'clamp(20px, 4vw, 32px)',
      }}
    >
      <img
        src="/icon-192.png"
        alt=""
        width={40}
        height={40}
        style={{ borderRadius: '10px', display: 'block' }}
      />
      <span
        style={{
          fontSize: 'clamp(20px, 5vw, 24px)',
          fontWeight: 900,
          color: INK,
          letterSpacing: '0.02em',
        }}
      >
        キンログ
      </span>
    </Link>

    <div
      style={{
        width: '100%',
        maxWidth: '440px',
        background: '#FFFFFF',
        borderRadius: '18px',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
        border: '1px solid #E2E8F0',
        padding: 'clamp(24px, 5vw, 36px)',
      }}
    >
      <h1
        style={{
          margin: 0,
          fontSize: 'clamp(19px, 4.5vw, 23px)',
          fontWeight: 700,
          color: INK,
        }}
      >
        {title}
      </h1>
      {description && (
        <p style={{ margin: '8px 0 0', fontSize: '13px', color: MUTED, lineHeight: 1.7 }}>
          {description}
        </p>
      )}

      <div style={{ marginTop: 'clamp(18px, 4vw, 24px)' }}>{children}</div>

      {footer && (
        <div
          style={{
            marginTop: '20px',
            paddingTop: '18px',
            borderTop: '1px solid #E2E8F0',
            fontSize: '13px',
            color: MUTED,
            textAlign: 'center',
          }}
        >
          {footer}
        </div>
      )}
    </div>

    <div
      style={{
        marginTop: '24px',
        fontSize: '12px',
        color: MUTED,
        display: 'flex',
        gap: '14px',
        flexWrap: 'wrap',
        justifyContent: 'center',
      }}
    >
      <Link to="/contact" style={{ color: MUTED }}>
        お問い合わせ
      </Link>
      <Link to="/terms" style={{ color: MUTED }}>
        利用規約
      </Link>
      <Link to="/privacy" style={{ color: MUTED }}>
        プライバシーポリシー
      </Link>
      <Link to="/legal" style={{ color: MUTED }}>
        特定商取引法に基づく表記
      </Link>
    </div>
    <div style={{ marginTop: '10px', fontSize: '11px', color: MUTED }}>
      © 株式会社立山システム
    </div>
  </div>
);

/** 認証画面で共通に使う入力欄スタイル。 */
export const authInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 12px',
  border: '1px solid #CBD5E1',
  borderRadius: '9px',
  fontSize: '15px',
  boxSizing: 'border-box',
  backgroundColor: '#FFFFFF',
};

/** 認証画面の主ボタン（ログイン・登録など）。 */
export const authPrimaryButtonStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px',
  backgroundColor: BRAND,
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '9px',
  fontSize: '15px',
  fontWeight: 700,
  cursor: 'pointer',
};

export const authLabelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '13px',
  fontWeight: 700,
  color: INK,
};

export { BRAND, BRAND_DARK, INK, MUTED };

export default AuthLayout;
